/**
 * [Phase 2c.6-A.2.2 RL-55] EmptySeatModal - pre-draft cascade prompted by
 * DraftLobby::startDraft when seat-status reports empty seats remain.
 *
 * Cascade states:
 *   1. "prompt"  - first prompt: Fill with bots? / Adjust team count / Cancel
 *   2. "adjust"  - inline picker, shrink num_teams to filled count, save → back to step 1
 *   3. "cancel"  - closes modal, returns to lobby with no DB writes
 *
 * Founder spec:
 *   - "Fill with bots" → POST add-cpus, then proceed to draft start
 *   - "Adjust team count" → PATCH team-count, refresh seat-status, return to prompt
 *   - "Cancel" → close modal, no draft start (preserves wizard/lobby state)
 *
 * Cleanliness rules:
 *   - Local state only. No global context dependency. Survives lobby remounts.
 *   - All actions go through props (onFill, onAdjustSaved, onCancel) so the
 *     parent owns side effects (toasts, axios errors, navigation).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { authHeaders } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmptySeatModal({
  leagueId,
  filled,
  empty,
  numTeams,
  onFill,
  onAdjusted,
  onCancel,
  isDark = true,
}) {
  const [phase, setPhase] = useState("prompt"); // prompt | adjust
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Picker initializes at the filled count so the operator's first move
  // (drop num_teams to exactly what's seated) is one click away.
  const [picker, setPicker] = useState(Math.max(2, filled));

  // Reset picker when filled/empty change (parent re-fetched seat-status).
  useEffect(() => {
    setPicker(Math.max(2, filled));
  }, [filled]);

  // Esc to cancel - accessibility parity with DraftEndRecapModal etc.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const overlayBg = isDark ? "rgba(0,0,0,0.78)" : "rgba(240,240,240,0.88)";
  const cardBg = isDark ? "#1A1A1A" : "#FFFFFF";
  const cardBorder = isDark ? "#2E2E2E" : "#D8D8D8";
  const titleColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const subColor = isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)";

  const handleFill = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API}/commissioner/${leagueId}/add-cpus`,
        { count: empty },
        { headers: authHeaders() }
      );
      onFill?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not fill empty seats");
    } finally {
      setBusy(false);
    }
  };

  const handleAdjustSave = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/commissioner/${leagueId}/team-count`,
        { count: picker },
        { headers: authHeaders() }
      );
      // Parent re-fetches seat-status and decides next state. Picker resets via useEffect.
      onAdjusted?.(picker);
      setPhase("prompt");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not adjust team count");
    } finally {
      setBusy(false);
    }
  };

  // Picker bounds: filled (can't shrink below seated) up to 14 (RL-55 MAX).
  const minPick = Math.max(2, filled);
  const maxPick = 14;
  const validOptions = [2, 4, 6, 8, 10, 12, 14].filter(
    (n) => n >= minPick && n <= maxPick
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: overlayBg, backdropFilter: "blur(6px)" }}
      data-testid="empty-seat-modal-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="rounded-2xl w-full max-w-md p-6 shadow-2xl"
        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        data-testid="empty-seat-modal-card"
      >
        {phase === "prompt" && (
          <>
            <h2
              className="font-display font-black uppercase text-xl tracking-wide mb-1"
              style={{ color: titleColor }}
              data-testid="empty-seat-modal-title"
            >
              {empty} Empty {empty === 1 ? "Seat" : "Seats"}
            </h2>
            <p className="font-body text-sm mb-5" style={{ color: subColor }}>
              {filled} of {numTeams} managers joined. Choose how to proceed.
            </p>

            {error && (
              <div
                className="mb-4 px-3 py-2 rounded-lg font-body text-xs"
                style={{
                  background: "rgba(255,68,68,0.12)",
                  border: "1px solid rgba(255,68,68,0.40)",
                  color: "#FF6B6B",
                }}
                data-testid="empty-seat-modal-error"
              >
                {error}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleFill}
                disabled={busy}
                data-testid="empty-seat-fill-bots-btn"
                className="w-full py-3 rounded-xl font-display font-black uppercase tracking-wider text-sm bg-[#00C853] hover:bg-[#00E676] disabled:opacity-50 text-[#111111] transition-all"
              >
                {busy ? "Working…" : `Fill ${empty} ${empty === 1 ? "seat" : "seats"} with bots`}
              </button>
              <button
                type="button"
                onClick={() => setPhase("adjust")}
                disabled={busy}
                data-testid="empty-seat-adjust-btn"
                className="w-full py-3 rounded-xl font-display font-bold uppercase tracking-wider text-sm bg-[#FF6B00]/15 hover:bg-[#FF6B00]/25 border border-[#FF6B00]/40 text-[#FF6B00] transition-all"
              >
                Adjust team count
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                data-testid="empty-seat-cancel-btn"
                className="w-full py-2.5 rounded-xl font-body text-sm transition-all"
                style={{
                  color: subColor,
                  border: `1px solid ${cardBorder}`,
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {phase === "adjust" && (
          <>
            <h2
              className="font-display font-black uppercase text-xl tracking-wide mb-1"
              style={{ color: titleColor }}
              data-testid="empty-seat-modal-title"
            >
              Adjust Team Count
            </h2>
            <p className="font-body text-sm mb-5" style={{ color: subColor }}>
              Shrink to match the {filled} {filled === 1 ? "manager" : "managers"} already seated.
              Minimum {minPick}, maximum {maxPick}.
            </p>

            {error && (
              <div
                className="mb-4 px-3 py-2 rounded-lg font-body text-xs"
                style={{
                  background: "rgba(255,68,68,0.12)",
                  border: "1px solid rgba(255,68,68,0.40)",
                  color: "#FF6B6B",
                }}
                data-testid="empty-seat-modal-error"
              >
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 mb-5">
              <label
                className="font-body text-sm shrink-0"
                style={{ color: titleColor }}
                htmlFor="empty-seat-picker"
              >
                Teams:
              </label>
              <select
                id="empty-seat-picker"
                value={picker}
                onChange={(e) => setPicker(parseInt(e.target.value, 10))}
                disabled={busy}
                data-testid="empty-seat-picker"
                className="flex-1 px-3 py-2 rounded-lg font-display font-bold text-base outline-none"
                style={{
                  background: isDark ? "#0F0F12" : "#F4F4F4",
                  border: `1px solid ${cardBorder}`,
                  color: titleColor,
                }}
              >
                {validOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleAdjustSave}
                disabled={busy || picker === numTeams}
                data-testid="empty-seat-adjust-save-btn"
                className="w-full py-3 rounded-xl font-display font-black uppercase tracking-wider text-sm bg-[#00C853] hover:bg-[#00E676] disabled:opacity-50 text-[#111111] transition-all"
              >
                {busy ? "Saving…" : `Save · ${picker} teams`}
              </button>
              <button
                type="button"
                onClick={() => { setPhase("prompt"); setError(null); }}
                disabled={busy}
                data-testid="empty-seat-adjust-back-btn"
                className="w-full py-2.5 rounded-xl font-body text-sm transition-all"
                style={{
                  color: subColor,
                  border: `1px solid ${cardBorder}`,
                }}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
